import { inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { CanActivateFn, Router, UrlTree } from "@angular/router";
import { firstValueFrom } from "rxjs";

import { ErrorPageCode, ErrorPageSubCode } from "../../app.enums";

import {
  ActivityIndicatorService,
  AppointmentService,
  extractErrorMessage,
  IAppointment,
  IAppointmentRouting,
  ITicket,
  PublicTicketService,
  ThemeService
} from "../../../core-ui";
import { PARAM_TICKET_ID } from "../../../core-ui/core-ui.enums";

class HandledError extends Error {
  subcode: string;
  code: any;

  constructor(
    code: ErrorPageCode,
    message?: string,
    subcode?: ErrorPageSubCode
  ) {
    super(message || code);
    this.code = code;
    this.subcode = subcode;
  }
}

export const appointmentManageGuard: CanActivateFn = async (
  route
): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const activity = inject(ActivityIndicatorService);
  const appointmentService = inject(AppointmentService);
  const themeService = inject(ThemeService);
  const ticketService = inject(PublicTicketService);

  const handleAppointment = (
    appointment: IAppointment<IAppointmentRouting>
  ) => {
    if (!appointment) {
      throw new HandledError(
        ErrorPageCode.APPOINTMENT_NOT_FOUND,
        "Appointment could not be found"
      );
    }

    if (appointment.interaction.state === "COMPLETE") {
      throw new HandledError(
        ErrorPageCode.APPOINTMENT_COMPLETED,
        "Appointment is completed"
      );
    }

    if (appointment.interaction.state === "EXPIRED") {
      throw new HandledError(
        ErrorPageCode.APPOINTMENT_EXPIRED,
        "Appointment has expired"
      );
    }

    appointmentService.notifyChange(appointment);
  };

  const onError = (
    error,
    appointmentId?: string,
    userMessage?: string,
    chatMode?: string,
    routingType?: string,
    participantId?: string
  ): UrlTree => {
    activity.loading(false);

    let errorPageCode, code, message;

    if (error instanceof HandledError) {
      code = error.code;
      errorPageCode = error.code;
      message = error.message;
    } else {
      code =
        error.responseJSON.status ||
        error.response.status ||
        ErrorPageCode.GENERIC_ERROR;
      errorPageCode = ErrorPageCode.GENERIC_ERROR;
      message = extractErrorMessage(error);
    }
    // log error to appointment as well
    if (appointmentId && code !== ErrorPageCode.APPOINTMENT_COMPLETED) {
      let systemMessage = `Error code: ${code}, message: ${message}`;
      if (chatMode) systemMessage += `, integration: ${chatMode}`;
      if (routingType) systemMessage += `, routing type: ${routingType}`;

      // update state
      // appointmentService.updateState(appointmentId, "failed", {
      //   systemMessage,
      //   userMessage
      // });

      // TODO: commented out for now as the backend is not yet ready
      // comment in when issue #766 is ready
      /*
        if (participantId) {
          appointmentService
            .participantFailed(appointmentId, participantId, {
              systemMessage,
              userMessage: userMessage || "Could not start a new conversation",
            })
            .catch(() => {});
        }
        */
    }
    return router.createUrlTree(["/error", errorPageCode], {
      queryParams: { message: userMessage }
    });
  };

  const getTicket = async (ticket: string): Promise<ITicket> => {
    try {
      if (!ticket) {
        throw new HandledError(
          ErrorPageCode.TICKET_NOT_FOUND,
          "Ticket not found in URL"
        );
      }

      const ticketData = await ticketService.getTicket(ticket);

      if (ticketData.properties.theme) {
        themeService.setOptions(ticketData.properties.theme);
      }

      return ticketData;
    } catch (ex) {
      throw new HandledError(
        ErrorPageCode.TICKET_NOT_FOUND,
        "Could not retrieve ticket details"
      );
    }
  };

  const getAppointment = async (
    appointmentId: string
  ): Promise<IAppointment<IAppointmentRouting>> => {
    try {
      const appointment = await appointmentService.get(appointmentId);
      handleAppointment(appointment);
      return appointment;
    } catch (ex) {
      if (ex instanceof HandledError) throw ex;
      else {
        if (ex?.status === 404) {
          throw new HandledError(
            ErrorPageCode.APPOINTMENT_NOT_FOUND,
            "Appointment not found"
          );
        } else if (ex?.status === 403) {
          throw new HandledError(
            ErrorPageCode.APPOINTMENT_MANAGE_DENIED,
            "Appointment cannot be accessed"
          );
        } else {
          throw new HandledError(
            ErrorPageCode.APPOINTMENT_ERROR,
            "Could not retrieve appointment details"
          );
        }
      }
    }
  };

  let appointmentId: string,
    appointment: IAppointment<IAppointmentRouting>,
    participantId: string,
    ticket: ITicket;

  activity.loading(true);

  const ticketCode = route.paramMap.get(PARAM_TICKET_ID);

  // 1. get the ticket data
  try {
    ticket = await getTicket(ticketCode);
    if (!ticket.properties.appointmentId) {
      // todo
    }
    appointmentId = ticket.properties.appointment_id;
    participantId = ticket.properties.customer_id;
  } catch (ex) {
    return onError(ex);
  }

  // 2. Check if someone used my ticket. If it was me on refresh I would be able to reconnect
  try {
    await getAppointment(appointmentId);
    //   if (
    //     appointment?.interaction?.initiatedBy?.id ===
    //       ticket.properties.customer_id &&
    //     // since I started it, I should be able to reconnect (have data in session store)
    //     !conversation.isReconnect(ticketCode)
    //   ) {
    //     return goToRoomAsGuestCustomer(ticket, appointment);
    //   }
    return true;
  } catch (e) {
    return onError(
      e,
      appointmentId,
      "Could not process appointment request",
      undefined,
      undefined,
      participantId
    );
  } finally {
    activity.loading(false);
  }
};
